import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as providerService from "@/shared/api/services/providerService";
import { auth } from "@/shared/api/graphqlClient";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Progress } from "@/shared/components/ui/progress";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName } from "@/shared/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Power,
  PowerOff,
  Server,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Zap,
  Activity,
} from "lucide-react";
import ProviderForm from "@/shared/components/providers/ProviderForm";

const statusConfig = {
  active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  inactive: { icon: XCircle, color: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
  maintenance: { icon: Clock, color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  degraded: { icon: AlertTriangle, color: "bg-red-100 text-red-700", dot: "bg-red-500" },
};

const typeColors = {
  bank: "bg-blue-100 text-blue-700",
  ewallet: "bg-purple-100 text-purple-700",
  qr: "bg-emerald-100 text-emerald-700",
  card: "bg-amber-100 text-amber-700",
};

export default function Providers() {
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const { t, language } = useLanguage();

  // Helper function to translate provider status
  const translateStatus = (status) => {
    const statusMap = {
      "active": t("active"),
      "inactive": t("inactive"),
      "degraded": t("degraded"),
      "maintenance": t("maintenance"),
    };
    return statusMap[status] || status;
  };

  const queryClient = useQueryClient();

  // Invalidate and refetch providers when component mounts to ensure fresh data
  useEffect(() => {
    if (auth.isAuthenticated()) {
      queryClient.invalidateQueries({ queryKey: ["providers"] });
    }
  }, [queryClient]);

  const { data: providers = [], isLoading, error } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
    enabled: auth.isAuthenticated(), // Only run query if authenticated
    retry: 2,
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus (to match global config)
    staleTime: 0, // Consider data stale immediately to force fresh fetch
    onError: (error) => {
      console.error('Providers query error:', error);
    },
    onSuccess: (data) => {
      console.log('Providers query success:', data);
    },
  });

  // Note: Create, update, delete mutations will be implemented later
  const createMutation = useMutation({
    mutationFn: async (data) => {
      // TODO: Implement provider creation mutation
      throw new Error("Provider creation not yet implemented");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["providers"]);
      setFormOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      // TODO: Implement provider update mutation
      throw new Error("Provider update not yet implemented");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["providers"]);
      setFormOpen(false);
      setSelectedProvider(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      // TODO: Implement provider delete mutation
      throw new Error("Provider deletion not yet implemented");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["providers"]);
      setDeleteDialog(null);
    },
  });

  const filteredProviders = providers.filter((p) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const providerName = getProviderName(p, language);
    return (
      providerName?.toLowerCase().includes(searchLower) ||
      p.code?.toLowerCase().includes(searchLower) ||
      p.name?.toLowerCase().includes(searchLower) ||
      p.nameEn?.toLowerCase().includes(searchLower)
    );
  });

  const handleSave = async (data) => {
    if (selectedProvider) {
      await updateMutation.mutateAsync({ id: selectedProvider.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleEdit = (provider) => {
    setSelectedProvider(provider);
    setFormOpen(true);
  };

  const handleToggleStatus = async (provider) => {
    const newStatus = provider.status === "active" ? "inactive" : "active";
    await updateMutation.mutateAsync({ id: provider.id, data: { ...provider, status: newStatus } });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("paymentProviders")}</h1>
          <p className="text-slate-500">
            {providers.filter((p) => p.status === "active").length} {t("activeProviders")}
          </p>
        </div>
        <Button onClick={() => setFormOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          {t("addProvider")}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t("searchProviders")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <p className="font-medium">Error loading providers:</p>
          <p className="text-sm">{error.message}</p>
          <p className="text-xs mt-2">Check browser console for more details.</p>
        </div>
      )}

      {/* Provider Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full text-center py-12 text-slate-500">
            {t("loading")}
          </div>
        ) : filteredProviders.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-500">
            {providers.length === 0 
              ? "No providers found. Make sure the backend is running and providers are seeded."
              : `No providers match "${search}"`}
          </div>
        ) : (
          filteredProviders.map((provider, index) => {
            const status = statusConfig[provider.status] || statusConfig.active;
            const StatusIcon = status.icon;

            return (
              <motion.div
                key={provider.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Server className="h-6 w-6 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{getProviderName(provider, language)}</h3>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs font-mono">
                          {provider.code}
                        </Badge>
                        <Badge className={`${typeColors[provider.type]} text-xs border-0`}>
                          {provider.type}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(provider)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        {t("edit")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleStatus(provider)}>
                        {provider.status === "active" ? (
                          <>
                            <PowerOff className="h-4 w-4 mr-2" />
                            {t("disable")}
                          </>
                        ) : (
                          <>
                            <Power className="h-4 w-4 mr-2" />
                            {t("enable")}
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setDeleteDialog(provider)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t("delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Status */}
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-2 h-2 rounded-full ${status.dot} animate-pulse`} />
                  <span className="text-sm text-slate-600">{translateStatus(provider.status)}</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-sm text-slate-600">{t("priority")} {provider.priority}</span>
                </div>

                {/* Metrics */}
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-500">{t("successRate")}</span>
                      <span className="font-medium">{provider.success_rate || 100}%</span>
                    </div>
                    <Progress value={provider.success_rate || 100} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-500">{t("healthScore")}</span>
                      <span className="font-medium">{provider.health_score || 100}</span>
                    </div>
                    <Progress value={provider.health_score || 100} className="h-2" />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-slate-500">
                      <Zap className="h-3 w-3" />
                      {t("response")}
                    </div>
                    <span className="font-medium">{provider.avg_response_time || 0}ms</span>
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                  {provider.supports_deposit && (
                    <Badge variant="outline" className="text-xs">{t("deposit")}</Badge>
                  )}
                  {provider.supports_withdrawal && (
                    <Badge variant="outline" className="text-xs">{t("withdrawal")}</Badge>
                  )}
                  {provider.supports_qr && (
                    <Badge variant="outline" className="text-xs">QR</Badge>
                  )}
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Provider Form Dialog */}
      <ProviderForm
        provider={selectedProvider}
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedProvider(null);
        }}
        onSave={handleSave}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteProvider")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteConfirmation")} "{deleteDialog?.name}"? {t("affectRouting")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(deleteDialog.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}